'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Add01Icon } from '@terra/ui-icons';
import { Button, KnowledgeSourcesCard } from '@terra/ui-design-system';
import { useAppKnowledgeSources } from '../../../hooks/api/useAppKnowledgeSources';
import { useOpenWebAppPage } from '../../../hooks/useOpenWebAppPage';
import { buildApplicationSettingsWebPath } from '../../../utils/applicationSettingsWebPaths';
import { useSettingsTabHeaderActions } from '../settingsTabHeaderContext';
import { toKnowledgeSourcesCardSources } from './businessContext.transforms';

type TargetKnowledgeSourcesPanelProps = {
  applicationId: string;
  isAiApplication: boolean;
  isReadOnly: boolean;
};

export function TargetKnowledgeSourcesPanel({
  applicationId,
  isAiApplication,
  isReadOnly,
}: TargetKnowledgeSourcesPanelProps) {
  const tCommon = useTranslations('common');
  const openWebAppPage = useOpenWebAppPage();
  const { sources, isLoading, error, removeProvider } = useAppKnowledgeSources({
    applicationId,
  });
  const cardSources = useMemo(
    () => toKnowledgeSourcesCardSources(sources),
    [sources],
  );
  const openWebKnowledgeSources = useCallback(() => {
    openWebAppPage(
      buildApplicationSettingsWebPath({
        applicationId,
        isAiApplication,
        intent: 'businessContext',
        subtab: 'knowledgeSources',
      }),
    );
  }, [applicationId, isAiApplication, openWebAppPage]);
  const headerActions = useMemo(
    () =>
      cardSources.length > 0 && !isReadOnly ? (
        <Button size="xs" variant="ghost" onClick={openWebKnowledgeSources}>
          <Add01Icon size={16} />
          {tCommon('add')}
        </Button>
      ) : null,
    [cardSources.length, isReadOnly, openWebKnowledgeSources, tCommon],
  );

  useSettingsTabHeaderActions(headerActions);

  return (
    <KnowledgeSourcesCard
      intent="settings"
      sources={cardSources}
      showEmptyState={!isLoading && !error && cardSources.length === 0}
      onAdd={openWebKnowledgeSources}
      onEdit={openWebKnowledgeSources}
      onRemove={removeProvider}
      isReadOnly={isReadOnly}
    />
  );
}
